import React, { useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight ? $gridheight * 100 : 400)}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-tooltip span {
    z-index: 9999 !important;
  }
  .tooltip {
    padding: 2px;
    background-color: var(--background-chart-tooltip);
    opacity: 1;
    z-index: 9999 !important;
  }
`;

export default function StackedAreaChart({
  data0,
  data1,
  name0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  name1,
  size,
  prism,
  filterValues,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  chartYtitle2,
  tooltipCfg,
  gridHeight,
  color0,
  color1,
  doneHide = false,
}: any) {
  const chartRef = useRef<any>(null);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: 'area',
        animation: false,
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
      },
      xAxis: {
        type: 'datetime',
        ordinal: false,
      },
      rangeSelector: {
        inputEnabled: false,
        selected: 5,
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 10,
        style: {
          fontSize: '16px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
      },
      subtitle: {
        text: chartSubtitle,
      },
      credits: {
        enabled: false,
      },
      legend: {
        reversed: true,
        enabled: true,
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 0,
        floating: true,
        backgroundColor: HighchartsReact.defaultProps?.defaultOptions?.legend.backgroundColor || 'white',
        shadow: false,
      },
      yAxis: [
        {
          title: {
            text: chartYtitle,
          },
          opposite: true,
          offset: 30,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: color0,
            },
            align: 'right',
            x: 10,
            y: -6,
          },
          margin: 30,
        },
        {
          title: {
            text: chartYtitle2,
          },
          opposite: false,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: color1,
            },
            align: 'right',
            x: -10,
            y: -6,
          },
          margin: 30,
        },
      ],
      tooltip: highchartsTooltipCfg(tooltipCfg),
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: 'var(--color-chart-no-data)',
        },
      },
      series: [
        {
          type: 'area',
          name: name0,
          data: data0,
        },
      ],
      plotOptions: {
        series: {
          selected: false,
          showInNavigator: true,
          animation: false,
          line: {
            selected: false,
          },
        },
        area: {
          stacking: 'normal',
          lineColor: 'var(--color-chart-options)',
          lineWidth: 1,
          marker: {
            lineWidth: 1,
            lineColor: 'var(--color-chart-options)',
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              yAxis: [
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  visible: false,
                },
              ],
            },
          },
        ],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data0, data1, tooltipCfg, chartTitle, chartSubtitle, chartYtitle, exportFileName, doneHide]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={Highcharts}
          constructorType={'chart'}
          options={chartOptions ?? EMPTY_OPTIONS}
          callback={(chart: any) => (chartRef.current = chart)}
        />
      </HighchartContainer>
    </Root>
  );
}
